import React from "react";
import LaunchIcon from "@mui/icons-material/Launch";
import SearchIcon from "@mui/icons-material/Search";
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  InputAdornment,
  LinearProgress,
  Paper,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Tabs,
  TextField,
} from "@mui/material";

export default function UserRegistrationsLotsDialog({
  open,
  onClose,
  modalPaperSx,
  primary,
  userRegsUser,
  getDisplayBidderName,
  getDisplayUsername,
  onExportWorkbook,
  userLotsExportBusy,
  userRegsModalView,
  setUserRegsModalView,
  formatNumber2,
  toAbsInt,
  moneyPlain,
  userRegsTotals,
  userRegsChargebackSummary,
  userRegsErr,
  userRegsBusy,
  userRegsSortKey,
  setUserRegsSortKey,
  userRegsSortDir,
  setUserRegsSortDir,
  sortedUserRegsRows,
  safeToMillis,
  isChargebackEmailSent,
  getChargebackSignDetails,
  openChargebackSignModal,
  onOpenAuction,
  userLotsErr,
  userLotsSearchInput,
  setUserLotsSearchInput,
  runUserLotsSearch,
  userLotsBusy,
  userLotsContainerRef,
  onUserLotsScroll,
  userLotsSortKey,
  setUserLotsSortKey,
  userLotsSortDir,
  setUserLotsSortDir,
  applySortToggle,
  sortedUserLotsRows,
  userLotsAuctionNameMap,
  userLotsLoadingMore,
  userLotsHasMore,
}) {
  const registrationsLabel =
    getDisplayBidderName(userRegsUser) || getDisplayUsername(userRegsUser) || userRegsUser?.email || userRegsUser?.id || "";

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      disableEnforceFocus
      PaperProps={{ sx: modalPaperSx }}
    >
      <DialogTitle sx={{ fontWeight: 900 }}>
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1, flexWrap: "wrap" }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
            <Box>
              Registrations
              <Box component="span" sx={{ ml: 1, color: "#6b7280", fontWeight: 800, fontSize: 13 }}>
                {registrationsLabel}
              </Box>
            </Box>
            <button
              type="button"
              className="btn-primary"
              onClick={() => void onExportWorkbook()}
              disabled={userLotsExportBusy}
              style={{
                opacity: userLotsExportBusy ? 0.72 : 1,
                cursor: userLotsExportBusy ? "not-allowed" : "pointer",
              }}
            >
              {userLotsExportBusy ? "Preparing XLSX..." : "Download XLSX"}
            </button>
          </Box>

          <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", justifyContent: "flex-end" }}>
            {userRegsModalView === "registrations" ? (
              <>
                <Chip
                  size="small"
                  label={`Auctions: ${formatNumber2(toAbsInt(userRegsTotals.count))}`}
                  sx={{ borderRadius: 999, fontWeight: 900 }}
                  variant="outlined"
                />
                <Chip
                  size="small"
                  label={`Total Lots: ${formatNumber2(toAbsInt(userRegsTotals.totalLots))}`}
                  sx={{ borderRadius: 999, fontWeight: 900 }}
                  variant="outlined"
                />
                <Chip
                  size="small"
                  label={`Total Hammer: $${moneyPlain(userRegsTotals.totalHammer)}`}
                  sx={{ borderRadius: 999, fontWeight: 900 }}
                />
                <Box
                  sx={{
                    px: 1.35,
                    py: 0.55,
                    borderRadius: 999,
                    fontWeight: 900,
                    fontSize: 12,
                    background: userRegsChargebackSummary.bg,
                    border: userRegsChargebackSummary.border,
                    color: userRegsChargebackSummary.color,
                    display: "flex",
                    alignItems: "center",
                  }}
                >
                  {userRegsChargebackSummary.label}
                </Box>
              </>
            ) : null}
          </Box>
        </Box>
        <Tabs
          value={userRegsModalView}
          onChange={(_, next) => {
            setUserRegsModalView(next);
          }}
          sx={{
            mt: 1.5,
            minHeight: 36,
            "& .MuiTabs-indicator": { backgroundColor: primary, height: 3, borderRadius: 99 },
          }}
        >
          <Tab value="registrations" label="Registrations" sx={{ textTransform: "none", minHeight: 36, py: 0.25, fontWeight: 900 }} />
          <Tab value="lots" label="Lots" sx={{ textTransform: "none", minHeight: 36, py: 0.25, fontWeight: 900 }} />
        </Tabs>
      </DialogTitle>
      <DialogContent dividers>
        {userRegsModalView === "registrations" ? (
          <>
            {userRegsErr ? (
              <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>
                {userRegsErr}
              </Alert>
            ) : null}

            {userRegsBusy ? <LinearProgress sx={{ mb: 2 }} /> : null}

            <TableContainer component={Paper} sx={{ borderRadius: 3, border: "1px solid rgba(51,57,153,0.10)", maxHeight: "68vh", overflowY: "auto" }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow sx={{ "& .MuiTableCell-root": { backgroundColor: "#f8fafc" } }}>
                    <TableCell sx={{ fontWeight: 900, color: primary }}>
                      <TableSortLabel
                        active={userRegsSortKey === "auctionName"}
                        direction={userRegsSortKey === "auctionName" ? userRegsSortDir : "asc"}
                        onClick={() => {
                          if (userRegsSortKey === "auctionName") setUserRegsSortDir((dir) => (dir === "asc" ? "desc" : "asc"));
                          else {
                            setUserRegsSortKey("auctionName");
                            setUserRegsSortDir("asc");
                          }
                        }}
                      >
                        Auction
                      </TableSortLabel>
                    </TableCell>
                    <TableCell sx={{ fontWeight: 900, color: primary }}>
                      <TableSortLabel
                        active={userRegsSortKey === "auctionEndAt"}
                        direction={userRegsSortKey === "auctionEndAt" ? userRegsSortDir : "desc"}
                        onClick={() => {
                          if (userRegsSortKey === "auctionEndAt") setUserRegsSortDir((dir) => (dir === "asc" ? "desc" : "asc"));
                          else {
                            setUserRegsSortKey("auctionEndAt");
                            setUserRegsSortDir("desc");
                          }
                        }}
                      >
                        End
                      </TableSortLabel>
                    </TableCell>
                    <TableCell sx={{ fontWeight: 900, color: primary }}>
                      <TableSortLabel
                        active={userRegsSortKey === "lotsCount"}
                        direction={userRegsSortKey === "lotsCount" ? userRegsSortDir : "desc"}
                        onClick={() => {
                          if (userRegsSortKey === "lotsCount") setUserRegsSortDir((dir) => (dir === "asc" ? "desc" : "asc"));
                          else {
                            setUserRegsSortKey("lotsCount");
                            setUserRegsSortDir("desc");
                          }
                        }}
                      >
                        Lots
                      </TableSortLabel>
                    </TableCell>
                    <TableCell sx={{ fontWeight: 900, color: primary }}>
                      <TableSortLabel
                        active={userRegsSortKey === "totalHighBid"}
                        direction={userRegsSortKey === "totalHighBid" ? userRegsSortDir : "desc"}
                        onClick={() => {
                          if (userRegsSortKey === "totalHighBid") setUserRegsSortDir((dir) => (dir === "asc" ? "desc" : "asc"));
                          else {
                            setUserRegsSortKey("totalHighBid");
                            setUserRegsSortDir("desc");
                          }
                        }}
                      >
                        Hammer
                      </TableSortLabel>
                    </TableCell>
                    <TableCell sx={{ fontWeight: 900, color: primary }}>
                      <TableSortLabel
                        active={userRegsSortKey === "emailSentAt"}
                        direction={userRegsSortKey === "emailSentAt" ? userRegsSortDir : "desc"}
                        onClick={() => {
                          if (userRegsSortKey === "emailSentAt") setUserRegsSortDir((dir) => (dir === "asc" ? "desc" : "asc"));
                          else {
                            setUserRegsSortKey("emailSentAt");
                            setUserRegsSortDir("desc");
                          }
                        }}
                      >
                        Email Sent
                      </TableSortLabel>
                    </TableCell>
                    <TableCell sx={{ fontWeight: 900, color: primary }}>
                      <TableSortLabel
                        active={userRegsSortKey === "chargebackEmailSent"}
                        direction={userRegsSortKey === "chargebackEmailSent" ? userRegsSortDir : "desc"}
                        onClick={() => {
                          if (userRegsSortKey === "chargebackEmailSent") setUserRegsSortDir((dir) => (dir === "asc" ? "desc" : "asc"));
                          else {
                            setUserRegsSortKey("chargebackEmailSent");
                            setUserRegsSortDir("desc");
                          }
                        }}
                      >
                        Chargeback / Pickup Mail Sent
                      </TableSortLabel>
                    </TableCell>
                    <TableCell sx={{ fontWeight: 900, color: primary }}>
                      <TableSortLabel
                        active={userRegsSortKey === "signed"}
                        direction={userRegsSortKey === "signed" ? userRegsSortDir : "desc"}
                        onClick={() => {
                          if (userRegsSortKey === "signed") setUserRegsSortDir((dir) => (dir === "asc" ? "desc" : "asc"));
                          else {
                            setUserRegsSortKey("signed");
                            setUserRegsSortDir("desc");
                          }
                        }}
                      >
                        Signed
                      </TableSortLabel>
                    </TableCell>
                    <TableCell sx={{ fontWeight: 900, color: primary }}>View Sign</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 900, color: primary }}>Action</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {!userRegsBusy && sortedUserRegsRows.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={9}>
                        <Box sx={{ p: 4, textAlign: "center", color: "#6b7280" }}>No registrations found.</Box>
                      </TableCell>
                    </TableRow>
                  ) : (
                    sortedUserRegsRows.map((row) => {
                      const auctionId = String(row?.auctionId || row?.id || "").trim();
                      const auctionName = String(row?.auctionName || row?.auctionLabel || auctionId || "").trim();
                      const endMs = safeToMillis(row?.auctionEndAtMs ?? row?.auctionEndAt);
                      const lotsCount = toAbsInt(row?.lotsCount ?? row?.lotsWon ?? 0);
                      const hammer = Number(row?.totalHighBid ?? row?.hammerTotal ?? 0) || 0;
                      const sentMs = safeToMillis(row?.emailSentAt);
                      const chargebackEmailSent = isChargebackEmailSent(row);
                      const signDetails = getChargebackSignDetails(row);
                      return (
                        <TableRow key={`${auctionId}__${row?.bidderEmail || row?.emailLower || ""}`} hover>
                          <TableCell sx={{ fontWeight: 900, color: "#111827" }}>{auctionName || "—"}</TableCell>
                          <TableCell sx={{ color: "#374151" }}>{endMs ? new Date(endMs).toLocaleString() : "—"}</TableCell>
                          <TableCell>
                            <Chip size="small" label={formatNumber2(lotsCount)} sx={{ borderRadius: 999, fontWeight: 900 }} variant="outlined" />
                          </TableCell>
                          <TableCell sx={{ fontWeight: 900, color: "#111827" }}>${moneyPlain(hammer)}</TableCell>
                          <TableCell sx={{ color: "#374151" }}>{sentMs ? new Date(sentMs).toLocaleString() : "—"}</TableCell>
                          <TableCell>
                            <Chip
                              size="small"
                              label={chargebackEmailSent ? "True" : "False"}
                              sx={{
                                borderRadius: 999,
                                fontWeight: 900,
                                color: chargebackEmailSent ? "#166534" : "#991b1b",
                                background: chargebackEmailSent ? "rgba(22,163,74,0.14)" : "rgba(248,113,113,0.14)",
                                border: chargebackEmailSent ? "1px solid rgba(22,163,74,0.25)" : "1px solid rgba(220,38,38,0.25)",
                              }}
                            />
                          </TableCell>
                          <TableCell>
                            <Chip
                              size="small"
                              label={signDetails.signed ? "True" : "False"}
                              sx={{
                                borderRadius: 999,
                                fontWeight: 900,
                                color: signDetails.signed ? "#166534" : "#991b1b",
                                background: signDetails.signed ? "rgba(22,163,74,0.14)" : "rgba(248,113,113,0.14)",
                                border: signDetails.signed ? "1px solid rgba(22,163,74,0.25)" : "1px solid rgba(220,38,38,0.25)",
                              }}
                            />
                          </TableCell>
                          <TableCell>
                            <Button size="small" variant="outlined" sx={{ textTransform: "none", borderRadius: 999, fontWeight: 900 }} onClick={() => openChargebackSignModal(row)}>
                              View Sign
                            </Button>
                          </TableCell>
                          <TableCell align="right">
                            <Button
                              onClick={() => onOpenAuction({ auctionId, auctionName })}
                              size="small"
                              startIcon={<LaunchIcon />}
                              sx={{ textTransform: "none", borderRadius: 999, fontWeight: 900 }}
                            >
                              Open
                            </Button>
                          </TableCell>
                        </TableRow>
                      );
                    })
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </>
        ) : (
          <>
            {userLotsErr ? (
              <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>
                {userLotsErr}
              </Alert>
            ) : null}
            <Box sx={{ mb: 1.5, display: "flex", gap: 1, alignItems: "center", flexWrap: "wrap" }}>
              <TextField
                fullWidth
                size="small"
                label="Search Lot Title / Auction"
                placeholder="Type lot title or auction and press Enter"
                value={userLotsSearchInput}
                onChange={(e) => setUserLotsSearchInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key !== "Enter") return;
                  e.preventDefault();
                  void runUserLotsSearch();
                }}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton size="small" onClick={() => void runUserLotsSearch()}>
                        <SearchIcon fontSize="small" />
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
                sx={{ flex: "1 1 340px" }}
              />
            </Box>
            {userLotsBusy ? <LinearProgress sx={{ mb: 2 }} /> : null}
            <TableContainer
              ref={userLotsContainerRef}
              onScroll={(e) => {
                void onUserLotsScroll(e);
              }}
              component={Paper}
              sx={{ borderRadius: 3, border: "1px solid rgba(51,57,153,0.10)", maxHeight: 520, overflowY: "auto" }}
            >
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow sx={{ "& .MuiTableCell-root": { backgroundColor: "#f8fafc" } }}>
                    <TableCell sx={{ fontWeight: 900, color: primary }}>
                      <TableSortLabel active={userLotsSortKey === "auction"} direction={userLotsSortKey === "auction" ? userLotsSortDir : "asc"} onClick={() => applySortToggle(userLotsSortKey, setUserLotsSortKey, setUserLotsSortDir, "auction", "asc")}>
                        Auction
                      </TableSortLabel>
                    </TableCell>
                    <TableCell sx={{ fontWeight: 900, color: primary }}>
                      <TableSortLabel active={userLotsSortKey === "lot"} direction={userLotsSortKey === "lot" ? userLotsSortDir : "asc"} onClick={() => applySortToggle(userLotsSortKey, setUserLotsSortKey, setUserLotsSortDir, "lot", "asc")}>
                        Lot
                      </TableSortLabel>
                    </TableCell>
                    <TableCell sx={{ fontWeight: 900, color: primary }}>
                      <TableSortLabel active={userLotsSortKey === "title"} direction={userLotsSortKey === "title" ? userLotsSortDir : "asc"} onClick={() => applySortToggle(userLotsSortKey, setUserLotsSortKey, setUserLotsSortDir, "title", "asc")}>
                        Title
                      </TableSortLabel>
                    </TableCell>
                    <TableCell sx={{ fontWeight: 900, color: primary }}>
                      <TableSortLabel active={userLotsSortKey === "time"} direction={userLotsSortKey === "time" ? userLotsSortDir : "desc"} onClick={() => applySortToggle(userLotsSortKey, setUserLotsSortKey, setUserLotsSortDir, "time", "desc")}>
                        Time
                      </TableSortLabel>
                    </TableCell>
                    <TableCell sx={{ fontWeight: 900, color: primary }}>
                      <TableSortLabel active={userLotsSortKey === "highBid"} direction={userLotsSortKey === "highBid" ? userLotsSortDir : "desc"} onClick={() => applySortToggle(userLotsSortKey, setUserLotsSortKey, setUserLotsSortDir, "highBid", "desc")}>
                        High Bid
                      </TableSortLabel>
                    </TableCell>
                    <TableCell sx={{ fontWeight: 900, color: primary }}>
                      <TableSortLabel active={userLotsSortKey === "maxBid"} direction={userLotsSortKey === "maxBid" ? userLotsSortDir : "desc"} onClick={() => applySortToggle(userLotsSortKey, setUserLotsSortKey, setUserLotsSortDir, "maxBid", "desc")}>
                        Max Bid
                      </TableSortLabel>
                    </TableCell>
                    <TableCell sx={{ fontWeight: 900, color: primary }}>
                      <TableSortLabel active={userLotsSortKey === "bids"} direction={userLotsSortKey === "bids" ? userLotsSortDir : "desc"} onClick={() => applySortToggle(userLotsSortKey, setUserLotsSortKey, setUserLotsSortDir, "bids", "desc")}>
                        Bids
                      </TableSortLabel>
                    </TableCell>
                    <TableCell sx={{ fontWeight: 900, color: primary }}>
                      <TableSortLabel active={userLotsSortKey === "views"} direction={userLotsSortKey === "views" ? userLotsSortDir : "desc"} onClick={() => applySortToggle(userLotsSortKey, setUserLotsSortKey, setUserLotsSortDir, "views", "desc")}>
                        Views
                      </TableSortLabel>
                    </TableCell>
                    <TableCell sx={{ fontWeight: 900, color: primary }}>
                      <TableSortLabel active={userLotsSortKey === "watches"} direction={userLotsSortKey === "watches" ? userLotsSortDir : "desc"} onClick={() => applySortToggle(userLotsSortKey, setUserLotsSortKey, setUserLotsSortDir, "watches", "desc")}>
                        Watches
                      </TableSortLabel>
                    </TableCell>
                    <TableCell sx={{ fontWeight: 900, color: primary }}>
                      <TableSortLabel active={userLotsSortKey === "consignor"} direction={userLotsSortKey === "consignor" ? userLotsSortDir : "asc"} onClick={() => applySortToggle(userLotsSortKey, setUserLotsSortKey, setUserLotsSortDir, "consignor", "asc")}>
                        Consignor
                      </TableSortLabel>
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {!userLotsBusy && sortedUserLotsRows.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={10}>
                        <Box sx={{ p: 4, textAlign: "center", color: "#6b7280" }}>No lots found.</Box>
                      </TableCell>
                    </TableRow>
                  ) : (
                    sortedUserLotsRows.map((lot) => {
                      const auctionId = String(lot?.auctionId || "").trim();
                      const auctionName = String(lot?.auctionName || userLotsAuctionNameMap.get(auctionId) || auctionId || "—");
                      const createdAtMs = safeToMillis(lot?.createdAt ?? lot?.updatedAt);
                      const timeText = String(lot?.timeLeft || "").trim() || (createdAtMs ? new Date(createdAtMs).toLocaleString() : "—");
                      return (
                        <TableRow key={lot?.id || `${auctionId}__${lot?.lot || ""}__${lot?.title || ""}`} hover>
                          <TableCell sx={{ color: "#374151", fontWeight: 700 }}>{auctionName}</TableCell>
                          <TableCell sx={{ fontWeight: 900 }}>{lot?.lot || "—"}</TableCell>
                          <TableCell sx={{ color: "#374151" }}>{lot?.title || "—"}</TableCell>
                          <TableCell sx={{ color: "#374151" }}>{timeText}</TableCell>
                          <TableCell sx={{ fontWeight: 900 }}>${moneyPlain(lot?.highBid || 0)}</TableCell>
                          <TableCell sx={{ fontWeight: 900 }}>${moneyPlain(lot?.maxBid || 0)}</TableCell>
                          <TableCell sx={{ color: "#374151" }}>{formatNumber2(lot?.bids || 0)}</TableCell>
                          <TableCell sx={{ color: "#374151" }}>{formatNumber2(lot?.views || 0)}</TableCell>
                          <TableCell sx={{ color: "#374151" }}>{formatNumber2(lot?.watches || 0)}</TableCell>
                          <TableCell sx={{ color: "#374151" }}>{lot?.consignor || "—"}</TableCell>
                        </TableRow>
                      );
                    })
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", minHeight: 44, mt: 1 }}>
              {userLotsLoadingMore ? (
                <Box sx={{ width: "100%", maxWidth: 320 }}>
                  <LinearProgress />
                </Box>
              ) : userLotsHasMore ? (
                <Box sx={{ color: "#64748b" }}>Scroll to load more lots</Box>
              ) : (
                <Box sx={{ color: "#64748b" }}>End of lots</Box>
              )}
            </Box>
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} sx={{ textTransform: "none", borderRadius: 999, fontWeight: 900 }}>
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
}
